import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Search, Inbox, CheckSquare, Plus } from "lucide-react";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";

const Tab = ({ active, onClick, icon: Icon, label, badge }) => (
  <button
    onClick={onClick}
    className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-xs font-semibold transition-colors relative ${
      active
        ? "text-indigo-600 dark:text-indigo-400"
        : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
    }`}
  >
    <div className="relative">
      <Icon className="w-4 h-4" />
      {badge > 0 && (
        <span className="absolute -top-1.5 -right-2 min-w-4 h-4 px-0.5 rounded-full bg-indigo-600 text-white text-[9px] font-bold flex items-center justify-center">
          {badge}
        </span>
      )}
    </div>
    <span>{label}</span>
    {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-t" />}
  </button>
);

const TicketsManager = () => {
  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const searchInputRef = useRef();
  const { user } = useContext(AuthContext);
  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const { ticketId } = useParams();

  useEffect(() => {
    if (user.profile.toUpperCase() === "ADMIN") setShowAllTickets(true);
  }, []);

  // When a ticket is opened (e.g. after accepting a pending ticket),
  // switch to the "open" sub-tab so the chat is visible and selected in the list.
  useEffect(() => {
    if (!ticketId) return;
    setTab("open");
    setTabOpen((prev) => (prev === "pending" ? "open" : prev));
  }, [ticketId]);

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current?.focus();
      setSearchParam("");
    }
  }, [tab]);

  let searchTimeout;
  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    clearTimeout(searchTimeout);
    if (term === "") { setSearchParam(term); setTab("open"); return; }
    searchTimeout = setTimeout(() => setSearchParam(term), 500);
  };

  const applyPanelStyle = (status) => tabOpen !== status ? { width: 0, height: 0, overflow: "hidden" } : {};

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800">
      <NewTicketModal modalOpen={newTicketModalOpen} onClose={() => setNewTicketModalOpen(false)} />

      {/* Main Tabs */}
      <div className="flex border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <Tab active={tab === "open"} onClick={() => setTab("open")} icon={Inbox} label={i18n.t("tickets.tabs.open.title")} badge={openCount + pendingCount} />
        <Tab active={tab === "closed"} onClick={() => setTab("closed")} icon={CheckSquare} label={i18n.t("tickets.tabs.closed.title")} badge={0} />
        <Tab active={tab === "search"} onClick={() => setTab("search")} icon={Search} label={i18n.t("tickets.tabs.search.title")} badge={0} />
      </div>

      {/* Options Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
        {tab === "search" ? (
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              ref={searchInputRef}
              type="search"
              placeholder={i18n.t("tickets.search.placeholder")}
              onChange={handleSearch}
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        ) : (
          <>
            <button
              onClick={() => setNewTicketModalOpen(true)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold border border-indigo-300 text-indigo-600 dark:text-indigo-400 dark:border-indigo-700 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              {i18n.t("ticketsManager.buttons.newTicket")}
            </button>
            <Can
              role={user.profile}
              perform="tickets-manager:showall"
              permissions={user.permissions}
              yes={() => (
                <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 cursor-pointer sm:ml-auto">
                  <div
                    onClick={() => setShowAllTickets((p) => !p)}
                    className={`relative w-7 h-4 rounded-full transition-colors ${showAllTickets ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-600"}`}
                  >
                    <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${showAllTickets ? "translate-x-3.5" : "translate-x-0.5"}`} />
                  </div>
                  {i18n.t("tickets.buttons.showAll")}
                </label>
              )}
            />
          </>
        )}
        <TicketsQueueSelect
          selectedQueueIds={selectedQueueIds}
          userQueues={user?.queues}
          onChange={setSelectedQueueIds}
        />
      </div>

      {/* Tab Content */}
      {tab === "open" && (
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Sub-tabs: open / pending */}
          <div className="flex border-b border-slate-200 dark:border-slate-800">
            <button
              onClick={() => setTabOpen("open")}
              className={`flex-1 py-2 text-xs font-semibold transition-colors relative ${tabOpen === "open" ? "text-indigo-600 dark:text-indigo-400" : "text-slate-500 dark:text-slate-400"}`}
            >
              <span className="relative">
                {i18n.t("ticketsList.assignedHeader")}
                {openCount > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-indigo-600 text-white text-[9px] font-bold">{openCount}</span>
                )}
              </span>
              {tabOpen === "open" && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-t" />}
            </button>
            <button
              onClick={() => setTabOpen("pending")}
              className={`flex-1 py-2 text-xs font-semibold transition-colors relative ${tabOpen === "pending" ? "text-amber-600 dark:text-amber-400" : "text-slate-500 dark:text-slate-400"}`}
            >
              <span className="relative">
                {i18n.t("ticketsList.pendingHeader")}
                {pendingCount > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[9px] font-bold">{pendingCount}</span>
                )}
              </span>
              {tabOpen === "pending" && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500 rounded-t" />}
            </button>
          </div>
          <div className="flex-1 overflow-hidden relative">
            <div className="absolute inset-0" style={applyPanelStyle("open")}>
              <TicketsList status="open" showAll={showAllTickets} selectedQueueIds={selectedQueueIds} updateCount={setOpenCount} style={{ height: "100%" }} />
            </div>
            <div className="absolute inset-0" style={applyPanelStyle("pending")}>
              <TicketsList status="pending" selectedQueueIds={selectedQueueIds} updateCount={setPendingCount} style={{ height: "100%" }} />
            </div>
          </div>
        </div>
      )}
      {tab === "closed" && (
        <div className="flex-1 overflow-hidden">
          <TicketsList status="closed" showAll={true} selectedQueueIds={selectedQueueIds} style={{ height: "100%" }} />
        </div>
      )}
      {tab === "search" && (
        <div className="flex-1 overflow-hidden">
          <TicketsList searchParam={searchParam} showAll={true} selectedQueueIds={selectedQueueIds} style={{ height: "100%" }} />
        </div>
      )}
    </div>
  );
};

export default TicketsManager;
